import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Trophy } from 'lucide-react';

const CATEGORIES = ['Sport', 'Cardio', 'Mobility', 'Other'];

export default function AdminActivities() {
  const [acts, setActs] = useState([]);
  const [form, setForm] = useState({ name: '', category: 'Sport', enabled: true });
  const [editing, setEditing] = useState(null);

  const load = async () => { const r = await api.get('/activities'); setActs(r.data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name required');
    if (editing) await api.put(`/activities/${editing}`, form);
    else await api.post('/activities', form);
    toast.success(editing ? 'Updated' : 'Activity added');
    setForm({ name: '', category: 'Sport', enabled: true }); setEditing(null); load();
  };
  const edit = (a) => { setEditing(a.id); setForm({ name: a.name, category: a.category, enabled: a.enabled }); };
  const del = async (id) => { if (!confirm('Delete activity?')) return; await api.delete(`/activities/${id}`); load(); };
  const toggle = async (a) => { await api.put(`/activities/${a.id}`, { ...a, enabled: !a.enabled }); load(); };

  return (
    <div className="space-y-6" data-testid="admin-activities">
      <div className="flex items-center gap-3">
        <Trophy className="w-6 h-6 text-[var(--c-green-ink)]" />
        <div>
          <h1 className="font-display font-black text-2xl">Activities Library</h1>
          <p className="text-sm text-muted-foreground">Searchable activities clients can log as bonus (Padel, Golf, Swimming…).</p>
        </div>
      </div>

      <div className="regen-card-elevated p-5">
        <div className="label-mini mb-3">{editing ? 'Edit Activity' : 'Add Activity'}</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Activity name" data-testid="activity-name-input" className="bg-regen-card border-[var(--c-hair)] h-11" />
          <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
            <SelectTrigger className="bg-regen-card border-[var(--c-hair)] h-11" data-testid="activity-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={save} data-testid="save-activity-btn" className="h-11 bg-[var(--c-green)] text-[#081726] font-semibold">
            {editing ? <><Edit2 className="w-4 h-4 mr-1" /> Update</> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
          </Button>
        </div>
        {editing && <button onClick={() => { setEditing(null); setForm({ name: '', category: 'Sport', enabled: true }); }} className="text-xs text-muted-foreground mt-2">Cancel edit</button>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {acts.map(a => (
          <div key={a.id} className="regen-card-elevated p-4 flex items-center gap-3" data-testid={`activity-row-${a.id}`}>
            <div className="flex-1">
              <div className="font-medium">{a.name}</div>
              <div className="text-xs text-muted-foreground">{a.category}</div>
            </div>
            <Switch checked={a.enabled} onCheckedChange={() => toggle(a)} data-testid={`activity-toggle-${a.id}`} />
            <button onClick={() => edit(a)} className="text-muted-foreground hover:text-[#00C8FF]" data-testid={`activity-edit-${a.id}`}><Edit2 className="w-4 h-4" /></button>
            <button onClick={() => del(a.id)} className="text-muted-foreground hover:text-red-400" data-testid={`activity-del-${a.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
